import type { MutableRefObject, RefCallback, RefObject } from 'react';

type MutableRef<T> =
  | RefCallback<T | null>
  | MutableRefObject<T>
  | RefObject<T | null>
  | null
  | undefined;

/**
 * Merges multiple refs into one. Works with either callback or object refs.
 */
export function mergeRefs<T>(...refs: Array<MutableRef<T>>): MutableRef<T> {
  if (refs.length === 1 && refs[0]) {
    return refs[0];
  }

  return (value: T | null) => {
    let hasCleanup = false;

    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, value);
      if (typeof cleanup === 'function') {
        hasCleanup = true;
      }
      return cleanup;
    });

    if (hasCleanup) {
      return () => {
        cleanups.forEach((cleanup, i) => {
          if (typeof cleanup === 'function') {
            cleanup();
          } else {
            setRef(refs[i], null);
          }
        });
      };
    }
  };
}

function setRef<T>(ref: MutableRef<T>, value: T | null): void | (() => void) {
  if (typeof ref === 'function') {
    return ref(value);
  } else if (ref != null) {
    (ref as MutableRefObject<T | null>).current = value;
  }
}
